/**
 * OrbitHotspot2D Component
 * Renders a 2D HTML hotspot that follows a 3D position on the satellite orbit
 */

import { useRef, useState, useMemo, useEffect, memo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import * as THREE from 'three'
import { Vector3, SphereGeometry } from 'three'
import { useIframeStore } from '../../../store/iframeStore'
import './Hotspots2D.css'

// Detect 4K display (≥2560px wide) — used to scale inline-styled hotspot elements
function use4K() {
  const [is4K, setIs4K] = useState(() => window.matchMedia('(min-width: 2560px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 2560px)')
    const handler = (e) => setIs4K(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return is4K
}

// Detect mobile portrait — text labels are hidden at this breakpoint
function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(
    () => window.matchMedia('(max-width: 767px) and (orientation: portrait)').matches
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px) and (orientation: portrait)')
    const handler = (e) => setIsMobilePortrait(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isMobilePortrait
}

// Detect desktop standard (1367px–1919px) — hotspots are reduced 25% at this breakpoint
function useDesktopStandard() {
  const [isDesktopStandard, setIsDesktopStandard] = useState(
    () => window.matchMedia('(min-width: 1367px) and (max-width: 1919px)').matches
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1367px) and (max-width: 1919px)')
    const handler = (e) => setIsDesktopStandard(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktopStandard
}

// Read --hotspot-line-scale CSS variable from :root — allows media queries to scale line geometry
function useHotspotLineScale() {
  const mqTablet = '(min-width: 900px) and (max-width: 1366px) and (orientation: landscape)'
  const mqMobile = '(max-width: 767px) and (orientation: portrait)'
  const [isTabletLandscape, setIsTabletLandscape] = useState(() => window.matchMedia(mqTablet).matches)
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => window.matchMedia(mqMobile).matches)
  useEffect(() => {
    const tabletMedia = window.matchMedia(mqTablet)
    const mobileMedia = window.matchMedia(mqMobile)
    const tabletHandler = (e) => setIsTabletLandscape(e.matches)
    const mobileHandler = (e) => setIsMobilePortrait(e.matches)
    tabletMedia.addEventListener('change', tabletHandler)
    mobileMedia.addEventListener('change', mobileHandler)
    return () => {
      tabletMedia.removeEventListener('change', tabletHandler)
      mobileMedia.removeEventListener('change', mobileHandler)
    }
  }, [])
  if (isMobilePortrait) return 0.33
  if (isTabletLandscape) return 0.33
  return 1
}

// Shared pulse ring SVG — absolutely centred on the dot, real size so it's never clipped
function PulseRing({ color }) {
  return (
    <svg
      width="80"
      height="80"
      viewBox="-40 -40 80 80"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        overflow: 'visible',
        pointerEvents: 'none',
      }}
    >
      <circle cx="0" cy="0" r="4" fill="none" stroke={color} style={{ strokeWidth: 'var(--pulse-stroke-width, 1.5)' }} opacity="0">
        <animate attributeName="r"       values="4;36;36" dur="1.5s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.8;0;0" dur="1.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  )
}

// Dot with pulse — solid red dot (opacity always 1) + animated ring behind it
function PulseDot({ size, color }) {
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <PulseRing color={color} />
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: `0 0 8px ${color}`,
          position: 'relative',
        }}
      />
    </div>
  )
}

// 3D marker component with HTML overlay - renders inside Canvas
export function OrbitMarker({ 
  orbitRadius, 
  angle, 
  rotationX, 
  rotationZ, 
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick = null
}) {
  const groupRef = useRef()
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isDesktopStandard = useDesktopStandard()
  const fs = is4K ? '24px' : isDesktopStandard ? '12px' : '16px'
  const dot = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const gap = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isDesktopStandard ? '21px' : '28px'
  
  // Calculate 3D position on the TILTED orbit ring.
  // Html renders at the group's world origin, so we must apply rotationX/rotationZ
  // to the XZ-plane point directly — the group rotation alone does nothing for Html.
  const position = useMemo(() => {
    const x0 = Math.cos(angle) * orbitRadius
    const z0 = Math.sin(angle) * orbitRadius
    // Apply rotationX around X axis (y0 = 0 so terms drop out)
    const cosRX = Math.cos(rotationX)
    const sinRX = Math.sin(rotationX)
    const y1 = -z0 * sinRX
    const z1 = z0 * cosRX
    // Apply rotationZ around Z axis
    const cosRZ = Math.cos(rotationZ)
    const sinRZ = Math.sin(rotationZ)
    const x2 = x0 * cosRZ - y1 * sinRZ
    const y2 = x0 * sinRZ + y1 * cosRZ
    return new Vector3(x2, y2, z1)
  }, [angle, orbitRadius, rotationX, rotationZ])
  
  return (
    <group ref={groupRef} position={position}>
      <Html
        sprite
        zIndexRange={[1000, 1000]}
        style={{
          pointerEvents: 'auto',
          userSelect: 'none'
        }}
      >
        <div
          className="hotspot-3d-marker"
          onClick={onClick}
          style={{ position: 'relative', cursor: onClick ? 'pointer' : 'default' }}
        >
          {/* Brackets centered */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: gap,
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1'
            }}
          >
            <span style={{ color: bracketColor, lineHeight: '1' }}>[</span>
            <PulseDot size={dot} color={dotColor} />
            <span style={{ color: bracketColor, lineHeight: '1' }}>]</span>
          </div>
          {/* Text positioned absolutely */}
          {text && !isMobilePortrait && textPosition === 'left' && (
            <span style={{ 
              position: 'absolute',
              right: textOffset,
              top: 0,
              transform: 'translate(0, -50%)',
              color: textColor,
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1'
            }}>{text}</span>
          )}
          {text && !isMobilePortrait && textPosition === 'right' && (
            <span style={{ 
              position: 'absolute',
              left: textOffset,
              top: 0,
              transform: 'translate(0, -50%)',
              color: textColor,
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1'
            }}>{text}</span>
          )}
        </div>
      </Html>
    </group>
  )
}

// 2D HTML overlay component - renders outside Canvas
export function OrbitHotspot2DOverlay({ 
  screenPos, 
  dotColor, 
  bracketColor, 
  blink, 
  text = '', 
  textPosition = 'right', // 'left' or 'right'
  textColor = '#E3E3E3'
}) {
  // Don't render if not visible or position hasn't been initialized yet
  if (!screenPos.visible || screenPos.x <= 0 || screenPos.y <= 0) return null
  
  const hotspotContent = (
    <>
      <span className="hotspot-bracket" style={{ color: bracketColor }}>[</span>
      <PulseDot size="8px" color={dotColor} />
      <span className="hotspot-bracket" style={{ color: bracketColor }}>]</span>
    </>
  )
  
  return (
    <div
      className="orbit-hotspot-2d"
      style={{
        position: 'fixed',
        left: `${screenPos.x}px`,
        top: `${screenPos.y}px`,
        zIndex: 1000,
        fontFamily: 'monospace',
        fontSize: '16px',
        fontWeight: 'bold'
      }}
    >
      {/* Hotspot centered at exact position */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {hotspotContent}
      </div>
      
      {/* Text positioned absolutely relative to hotspot */}
      {text && textPosition === 'left' && (
        <span className="hotspot-text" style={{ 
          position: 'absolute',
          right: '28px',
          top: '50%',
          transform: 'translate(0, -50%)',
          color: textColor,
          whiteSpace: 'nowrap'
        }}>{text}</span>
      )}
      {text && textPosition === 'right' && (
        <span className="hotspot-text" style={{ 
          position: 'absolute',
          left: '28px',
          top: '50%',
          transform: 'translate(0, -50%)',
          color: textColor,
          whiteSpace: 'nowrap'
        }}>{text}</span>
      )}
    </div>
  )
}

// Static position marker with HTML overlay - for hotspots at fixed 3D positions
export function StaticMarker({ 
  position,
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick
}) {
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isDesktopStandard = useDesktopStandard()
  const fs = is4K ? '24px' : isDesktopStandard ? '12px' : '16px'
  const dot = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const gap = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isDesktopStandard ? '21px' : '28px'
  return (
    <group position={position}>
      <Html
        sprite
        zIndexRange={[1000, 1000]}
        style={{
          pointerEvents: onClick ? 'auto' : 'none',
          userSelect: 'none'
        }}
      >
        <div
          className="hotspot-3d-marker"
          onClick={onClick}
          style={{
            position: 'relative',
            cursor: onClick ? 'pointer' : 'default'
          }}
        >
          {/* Brackets centered */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: gap,
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1',
            }}
          >
            <span style={{ color: bracketColor, lineHeight: '1' }}>[</span>
            <PulseDot size={dot} color={dotColor} />
            <span style={{ color: bracketColor, lineHeight: '1' }}>]</span>
          </div>
          {/* Text positioned absolutely */}
          {text && !isMobilePortrait && textPosition === 'left' && (
            <span style={{ 
              position: 'absolute',
              right: textOffset,
              top: 0,
              transform: 'translate(0, -50%)',
              color: textColor,
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1'
            }}>{text}</span>
          )}
          {text && !isMobilePortrait && textPosition === 'right' && (
            <span style={{ 
              position: 'absolute',
              left: textOffset,
              top: 0,
              transform: 'translate(0, -50%)',
              color: textColor,
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1'
            }}>{text}</span>
          )}
        </div>
      </Html>
    </group>
  )
}

// Earth surface marker with HTML overlay - for hotspots at specific lat/long on rotating Earth.
// Uses the Earth's actual inner group world matrix to compute position, eliminating
// the need to replicate Earth's transform chain (tilt + rotation) independently.
// This prevents desync after render-loop pause/resume (iframe display).
export const EarthSurfaceMarker = memo(function EarthSurfaceMarker({ 
  latitude, 
  longitude, 
  latitudeOffset = 0, 
  longitudeOffset = 0, 
  earthRadius = 1, 
  earthInnerGroupRef,
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick = null,
  variant = 'brackets'
}) {
  const groupRef = useRef()
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isDesktopStandard = useDesktopStandard()
  const fs = is4K ? '24px' : isMobilePortrait ? '11px' : isDesktopStandard ? '12px' : '16px'
  const dot = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const gap = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isDesktopStandard ? '21px' : '28px'
  const svgScale = is4K ? 1.5 : isDesktopStandard ? 0.75 : 1

  // Hide the HTML overlay when an iframe is open — keeps useFrame updating
  // so when the iframe closes the marker is already at the correct position.
  const markersVisible = useIframeStore(state => state.markersVisible)

  // Reusable vectors to avoid allocations every frame
  const _localPos = useMemo(() => new Vector3(), [])
  const _worldPos = useMemo(() => new Vector3(), [])

  // Priority -1 ensures this runs BEFORE Drei's <Html> useFrame (priority 0),
  // so the world position is up-to-date when Drei samples it for projection.
  useFrame(() => {
    if (!groupRef.current || !earthInnerGroupRef?.current) return

    // Convert lat/long (with offsets) to local-space position on sphere
    const lat = (latitude + latitudeOffset) * (Math.PI / 180)
    const lon = (longitude + longitudeOffset) * (Math.PI / 180)

    _localPos.set(
      earthRadius * Math.cos(lat) * Math.cos(lon),
      earthRadius * Math.sin(lat),
      earthRadius * Math.cos(lat) * Math.sin(lon)
    )

    // Transform through Earth's world matrix (includes tilt + spin rotation)
    earthInnerGroupRef.current.updateWorldMatrix(true, false)
    _worldPos.copy(_localPos).applyMatrix4(earthInnerGroupRef.current.matrixWorld)

    groupRef.current.position.copy(_worldPos)
  }, -1)

  return (
    <group ref={groupRef}>
      <Html
        sprite
        zIndexRange={[1000, 1000]}
        style={{
          pointerEvents: markersVisible ? 'auto' : 'none',
          userSelect: 'none'
        }}
      >
        <div className="hotspot-3d-marker" style={{ position: 'relative', cursor: onClick ? 'pointer' : 'default', display: markersVisible ? undefined : 'none' }} onClick={onClick}>
          {variant === 'line' ? (
            /* Right-aligned: text ────╱─■ where ■ is at the anchor point */
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                transform: 'translate(-100%, -50%)',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '1'
              }}
            >
              {/* Text */}
              {text && (
                <span style={{
                  marginRight: '10px',
                  color: dotColor,
                  fontFamily: 'monospace',
                  fontSize: fs,
                  fontWeight: 'bold',
                  letterSpacing: '1.5px',
                  whiteSpace: 'nowrap',
                  lineHeight: '1',
                  textTransform: 'uppercase',
                }}>{text}</span>
              )}
              {/* SVG: long horizontal → diagonal up → short horizontal → square with ping */}
              <svg
                width={80 * svgScale}
                height={14 * svgScale}
                viewBox="0 0 80 14"
                style={{ flexShrink: 0, display: 'block', overflow: 'visible' }}
              >
                <polyline
                  points="0,7 38,7 52,3 73,3"
                  stroke={dotColor}
                  strokeWidth="1"
                  fill="none"
                  strokeLinejoin="miter"
                />
                <circle cx="76.5" cy="3.5" r="3" fill="none" stroke={dotColor} style={{ strokeWidth: 'var(--pulse-stroke-width, 1.5)' }} opacity="0">
                  <animate attributeName="r" values="3;36;36" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0;0" dur="1.5s" repeatCount="indefinite" />
                </circle>
                <rect x="73" y="0" width="7" height="7" fill={dotColor} style={{ filter: `drop-shadow(0 0 4px ${dotColor})` }} />
              </svg>
            </div>
          ) : (
            <>
              {/* Brackets centered */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: gap,
                  fontFamily: 'monospace',
                  fontSize: fs,
                  fontWeight: 'bold',
                  lineHeight: '1'
                }}
              >
            <span style={{ color: bracketColor, lineHeight: '1' }}>[</span>
            <PulseDot size={dot} color={dotColor} />
            <span style={{ color: bracketColor, lineHeight: '1' }}>]</span>
          </div>
          {/* Text positioned absolutely */}
          {text && !isMobilePortrait && textPosition === 'left' && (
            <span style={{ 
                  position: 'absolute',
                  right: textOffset,
                  top: 0,
                  transform: 'translate(0, -50%)',
                  color: textColor,
                  whiteSpace: 'nowrap',
                  fontFamily: 'monospace',
                  fontSize: fs,
                  fontWeight: 'bold',
                  lineHeight: '1'
                }}>{text}</span>
              )}
              {text && !isMobilePortrait && textPosition === 'right' && (
                <span style={{ 
                  position: 'absolute',
                  left: textOffset,
                  top: 0,
                  transform: 'translate(0, -50%)',
                  color: textColor,
                  whiteSpace: 'nowrap',
                  fontFamily: 'monospace',
                  fontSize: fs,
                  fontWeight: 'bold',
                  lineHeight: '1'
                }}>{text}</span>
              )}
            </>
          )}
        </div>
      </Html>
    </group>
  )
})

// Moon surface marker with HTML overlay - for hotspots at specific lat/long on static Moon
export function MoonSurfaceMarker({ 
  latitude, 
  longitude, 
  moonRadius = 0.183, 
  moonTilt = 1.54,
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick = null,
  variant = 'brackets'
}) {
  const markerRef = useRef()
  const outerGroupRef = useRef()
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isDesktopStandard = useDesktopStandard()
  const fs = is4K ? '24px' : isMobilePortrait ? '11px' : isDesktopStandard ? '12px' : '16px'
  const dot = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const gap = is4K ? '12px' : isDesktopStandard ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isDesktopStandard ? '21px' : '28px'
  const svgScale = is4K ? 1.5 : isDesktopStandard ? 0.75 : 1
  
  // Calculate position once on mount - Moon is static
  const position = useMemo(() => {
    const lat = latitude * (Math.PI / 180)
    const lon = longitude * (Math.PI / 180)
    
    const x = moonRadius * Math.cos(lat) * Math.cos(lon)
    const y = moonRadius * Math.sin(lat)
    const z = moonRadius * Math.cos(lat) * Math.sin(lon)
    
    return [x, y, z]
  }, [latitude, longitude, moonRadius])
  
  // Apply Moon's tilt rotation to outer group
  const tiltRotationRadians = moonTilt * (Math.PI / 180)
  
  return (
    <group ref={outerGroupRef} rotation={[0, 0, tiltRotationRadians]}>
      <group ref={markerRef} position={position}>
        <Html
          sprite
          zIndexRange={[1000, 1000]}
          style={{
            pointerEvents: onClick ? 'auto' : 'none',
            userSelect: 'none'
          }}
        >
          <div className="hotspot-3d-marker" style={{ position: 'relative', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
            {variant === 'line' ? (
              <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-100%, -50%)', display: 'flex', alignItems: 'center', lineHeight: '1', pointerEvents: onClick ? 'auto' : 'none' }}>
                {text && (
                  <span style={{ color: textColor, fontSize: fs, fontFamily: 'monospace', whiteSpace: 'nowrap', marginRight: '10px', fontWeight: 'bold', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{text}</span>
                )}
                <svg width={80 * svgScale} height={14 * svgScale} viewBox="0 0 80 14" style={{ flexShrink: 0, display: 'block', overflow: 'visible' }}>
                  <polyline points="0,7 38,7 52,3 73,3" stroke={dotColor} strokeWidth="1" fill="none" strokeLinejoin="miter" />
                  <circle cx="76.5" cy="3.5" r="3" fill="none" stroke={dotColor} style={{ strokeWidth: 'var(--pulse-stroke-width, 1.5)' }} opacity="0">
                    <animate attributeName="r" values="3;36;36" dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.8;0;0" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <rect x="73" y="0" width="7" height="7" fill={dotColor} style={{ filter: `drop-shadow(0 0 4px ${dotColor})` }} />
                </svg>
              </div>
            ) : (
              <>
                {/* Brackets centered */}
                <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)', display: 'flex', alignItems: 'center', gap: gap, fontFamily: 'monospace', fontSize: fs, fontWeight: 'bold', lineHeight: '1' }}>
                  <span style={{ color: bracketColor, lineHeight: '1' }}>[</span>
                  <PulseDot size={dot} color={dotColor} />
                  <span style={{ color: bracketColor, lineHeight: '1' }}>]</span>
                </div>
                {/* Text positioned absolutely */}
                {text && !isMobilePortrait && textPosition === 'left' && (
                  <span style={{ position: 'absolute', right: textOffset, top: 0, transform: 'translate(0, -50%)', color: textColor, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: fs, fontWeight: 'bold', lineHeight: '1' }}>{text}</span>
                )}
                {text && !isMobilePortrait && textPosition === 'right' && (
                  <span style={{ position: 'absolute', left: textOffset, top: 0, transform: 'translate(0, -50%)', color: textColor, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: fs, fontWeight: 'bold', lineHeight: '1' }}>{text}</span>
                )}
              </>
            )}
          </div>
        </Html>
      </group>
    </group>
  )
}

// Artemis tracker - for tracking the Artemis rocket position
export function ArtemisTracker({ onPositionUpdate }) {
  const { camera, size } = useThree()
  
  // Reusable Vector3 objects to avoid creating new objects every frame
  const worldPosRef = useRef(new Vector3())
  const screenPosRef = useRef(new Vector3())
  
  // Reusable position update object to avoid creating new objects every frame
  const posUpdateRef = useRef({ x: 0, y: 0, z: 0, visible: false })
  
  // Track last updated position to prevent unnecessary state updates
  const lastPosRef = useRef({ x: -1, y: -1, z: 0, visible: false })
  
  useFrame(() => {
    // Access the Artemis group from global window reference
    if (window.artemisGroupRef) {
      // Get world position of the Artemis group - reuse Vector3
      window.artemisGroupRef.getWorldPosition(worldPosRef.current)
      
      // Project 3D position to 2D screen coordinates - reuse Vector3
      screenPosRef.current.copy(worldPosRef.current).project(camera)
      
      // Convert to pixel coordinates - reuse object to prevent GC churn
      posUpdateRef.current.x = (screenPosRef.current.x * 0.5 + 0.5) * size.width
      posUpdateRef.current.y = (-(screenPosRef.current.y * 0.5) + 0.5) * size.height
      posUpdateRef.current.z = screenPosRef.current.z
      posUpdateRef.current.visible = screenPosRef.current.z < 1 && screenPosRef.current.z > -1
      
      // Only update if position changed significantly (threshold: 0.5 pixels)
      // This prevents jittery re-renders from sub-pixel movements
      const dx = Math.abs(posUpdateRef.current.x - lastPosRef.current.x)
      const dy = Math.abs(posUpdateRef.current.y - lastPosRef.current.y)
      const visibilityChanged = posUpdateRef.current.visible !== lastPosRef.current.visible
      
      if (dx > 0.5 || dy > 0.5 || visibilityChanged) {
        lastPosRef.current.x = posUpdateRef.current.x
        lastPosRef.current.y = posUpdateRef.current.y
        lastPosRef.current.z = posUpdateRef.current.z
        lastPosRef.current.visible = posUpdateRef.current.visible
        
        onPositionUpdate(posUpdateRef.current)
      }
    }
  })
  
  return null // This component doesn't render anything in 3D
}

// Artemis 2D overlay - renders square brackets around Artemis
export function Artemis2DOverlay({ 
  screenPos, 
  bracketColor = '#EE3A17',
  visible = true,
  blink = 1
}) {
  // Don't render if not visible or position hasn't been initialized yet
  if (!visible || !screenPos.visible || screenPos.x <= 0 || screenPos.y <= 0) return null
  
  return (
    <div
      style={{
        position: 'fixed',
        left: `${screenPos.x}px`,
        top: `${screenPos.y}px`,
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    >
      {/* Brackets centered at Artemis position */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        fontFamily: 'monospace',
        fontSize: '24px',
        fontWeight: 'bold',
        color: bracketColor,
        opacity: blink
      }}>
        <span>[</span>
        <span>]</span>
      </div>
    </div>
  )
}

export default OrbitMarker

/**
 * SatelliteHotspot
 *
 * A 3-segment callout line anchored at a satellite's 3D world center.
 * All geometry is configurable via the `line` prop (see locations.json).
 *
 * Props:
 *   satelliteGroupRef — ref to the satellite Three.js Group
 *   offset            — [x, y, z] world-space offset from group center
 *   direction         — "left" | "right"
 *   line              — geometry config object (all values in SVG/CSS pixels):
 *     seg1      — horizontal run from anchor square to start of diagonal
 *     seg2      — horizontal width of the diagonal segment
 *     diagDrop  — vertical rise/drop of the diagonal (positive = drops downward)
 *     seg3      — horizontal run from end of diagonal to text
 *   text              — label string
 *   color             — stroke/fill/text color (default brand red #EE3A17)
 *   visible           — show/hide without unmounting
 */
export function SatelliteHotspot({
  satelliteGroupRef,
  offset = [0, 0, 0],
  mobileOffset = null,
  direction = 'right',
  line = {},
  text = '',
  color = '#EE3A17',
  visible = true,
  onClick = null,
}) {
  const groupRef = useRef()
  const isMobilePortrait = useMobilePortrait()
  const isDesktopStandard = useDesktopStandard()
  const fs = isMobilePortrait ? '11px' : isDesktopStandard ? '12px' : '16px'

  // Use mobileOffset on mobile portrait if provided, otherwise fall back to offset
  const activeOffset = isMobilePortrait && mobileOffset ? mobileOffset : offset

  // Geometry — each value is independently named for easy JSON editing
  const lineScale = useHotspotLineScale()
  const seg1     = (line.seg1     ?? 38)  * lineScale  // first horizontal
  const seg2     = (line.seg2     ?? 14)  * lineScale  // diagonal horizontal span
  const diagDrop = (line.diagDrop ?? 4)   * lineScale * (isMobilePortrait ? -1 : 1)  // flipped on mobile portrait
  const seg3     = (line.seg3     ?? 21)  * lineScale  // second horizontal

  // SVG coordinate system — anchor square is always on the LEFT end.
  // For direction="left" we mirror the SVG with scaleX(-1) so the line runs left.
  const squareSize = 7
  const halfSq     = squareSize / 2
  const textGap    = 8   // px gap between line end and text

  // Y positions — line runs at the square's vertical centre, diagonal drops below
  const lineY   = halfSq                       // y of the main horizontal (top of square = 0)
  const diagEndY = lineY + diagDrop             // y at end of diagonal

  // X positions of each node along the polyline
  const x0 = halfSq                            // anchor square centre x
  const x1 = x0 + seg1                         // end of seg1
  const x2 = x1 + seg2                         // end of seg2 (diagonal)
  const x3 = x2 + seg3                         // end of seg3 (text start)

  // SVG canvas size — add right-side padding so the pulse ring is never clipped
  const svgW = x3 + textGap
  const svgH = Math.max(squareSize, lineY + Math.abs(diagDrop) + halfSq + 1)

  // Track satellite group world position each frame
  const _worldPos  = useMemo(() => new Vector3(), [])
  const _offsetVec = useMemo(
    () => new Vector3(activeOffset[0], activeOffset[1], activeOffset[2]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeOffset[0], activeOffset[1], activeOffset[2]]
  )

  useFrame(() => {
    if (!groupRef.current || !satelliteGroupRef?.current) return
    satelliteGroupRef.current.getWorldPosition(_worldPos)
    _worldPos.add(_offsetVec)
    groupRef.current.position.copy(_worldPos)
  }, -1)

  const isLeft = direction === 'left'

  // Container transform — anchor square centre sits at the Html origin (0,0).
  // For "right": shift left by x0 so the square is at origin, line extends right.
  // For "left":  SVG is mirrored (scaleX(-1)), so visually the square is on the
  //              right end of the flipped drawing. We still shift left by x0 so
  //              that right edge (the square) sits at origin.
  const translateX = isLeft ? -(svgW - x0) : -x0
  const translateY = -lineY

  return (
    <group ref={groupRef}>
      <Html zIndexRange={[999, 999]} style={{ pointerEvents: 'auto', userSelect: 'none' }}>
        <div style={{ display: visible ? 'block' : 'none' }}>
          <div
            className="satellite-hotspot"
            style={{
              transform: `translate(${translateX}px, ${translateY}px)`,
              cursor: onClick ? 'pointer' : 'default',
              pointerEvents: onClick ? 'auto' : 'none',
            }}
            onClick={onClick}
          >
            <svg
              width={svgW}
              height={svgH}
              viewBox={`0 0 ${svgW} ${svgH}`}
              style={{
                display: 'block',
                overflow: 'visible',
                transform: isLeft ? 'scaleX(-1)' : 'none',
              }}
            >
              {/* Pulse ring at anchor */}
              <circle cx={x0} cy={lineY} r={halfSq}
                fill="none" stroke={color}
                style={{ strokeWidth: 'var(--pulse-stroke-width, 1.5)' }}
                opacity="0"
              >
                <animate attributeName="r"       values={`${halfSq};24;24`} dur="1.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0;0"           dur="1.5s" repeatCount="indefinite" />
              </circle>
              {/* Anchor square */}
              <rect x={0} y={0} width={squareSize} height={squareSize}
                fill={color} style={{ filter: `drop-shadow(0 0 4px ${color})` }}
              />
              {/* 3-segment polyline: horizontal → diagonal → horizontal */}
              <polyline
                points={`${x0},${lineY} ${x1},${lineY} ${x2},${diagEndY} ${x3},${diagEndY}`}
                stroke={color} strokeWidth="1" fill="none" strokeLinejoin="miter"
              />
              {/* Terminal dot */}
              <circle cx={x3} cy={diagEndY} r="2" fill={color} />
            </svg>

            {/* Text — positioned absolutely so it aligns exactly to the line's far end */}
            {text && (
              <span
                className="satellite-hotspot-text"
                style={{
                  // For "right": text sits to the right of the SVG end point.
                  // For "left":  SVG is mirrored, so x3 is now visually on the left —
                  //              place text to the left of the container's left edge.
                  ...(isLeft
                    ? { right: svgW - x0 + textGap }
                    : { left:  svgW - x0 + textGap - translateX }),
                  // Vertically centre on the far end of the line
                  top: `${lineY + diagDrop}px`,
                  fontSize: fs,
                  color,
                }}
              >{text}</span>
            )}
          </div>
        </div>
      </Html>
    </group>
  )
}
